<script setup lang="ts">
/**
 * The hangar: three class paintings as big cards in a swipeable row; the
 * chosen class's hulls, stats and big render are `HullHangar.vue` under them.
 * Choosing a class (a tap, or a swipe that settles on a card) picks that
 * class's top hull.
 */
import { ref } from 'vue';

import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';
import { classArt } from '@/ui/panel/art-path';

import { FLEET_CLASSES, type ShipClass, hullsOfClass } from '../landing-content';
import HullHangar from './HullHangar.vue';
import SectionShell from './SectionShell.vue';

const { t } = useI18n();

const shipClass = ref<ShipClass>('battleship');
const hullId = ref('battleship_t4');
const rowEl = ref<HTMLElement | null>(null);

function pickClass(id: ShipClass): void {
  shipClass.value = id;
  const list = hullsOfClass(id);
  hullId.value = list[list.length - 1]!.id;
}

/** A swipe settles on a card: whichever card's centre is nearest the row's centre is the class. */
function onRowScroll(): void {
  const row = rowEl.value;
  if (!row || row.scrollWidth <= row.clientWidth + 2) return;
  const mid = row.scrollLeft + row.clientWidth / 2;
  let best: ShipClass = shipClass.value;
  let gap = Infinity;
  row.querySelectorAll<HTMLElement>('[data-class]').forEach((el) => {
    const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid);
    if (d < gap) {
      gap = d;
      best = el.dataset.class as ShipClass;
    }
  });
  if (best !== shipClass.value) pickClass(best);
}
</script>

<template>
  <SectionShell id="fleet" icon="warship" :title="t('landing.fleet.title')" :line="t('landing.fleet.line')" backdrop="/art/mode-convoy.webp" wide>
    <ul ref="rowEl" class="classes" :aria-label="t('landing.fleet.pick')" data-slam="rise" @scroll.passive="onRowScroll">
      <li v-for="c in FLEET_CLASSES" :key="c.id" class="slot" :data-class="c.id">
        <button
          type="button"
          class="card"
          :class="{ on: shipClass === c.id }"
          :aria-pressed="shipClass === c.id"
          @click="pickClass(c.id)"
        >
          <img class="art" :src="classArt(c.id)" :alt="t(`landing.fleet.class.${c.id}.alt`)" width="960" height="640" loading="lazy" decoding="async" />
          <span class="k-header name"><span class="k-title">{{ t(c.nameKey) }}</span></span>
          <span class="k-chip count"><GameIcon name="warship" :size="28" />{{ hullsOfClass(c.id).length }}</span>
        </button>
      </li>
    </ul>

    <HullHangar v-model:hull-id="hullId" :ship-class="shipClass" data-slam="rise" />
  </SectionShell>
</template>

<style scoped>
/* Phone: a swipeable row of big cards. Wide: all three at once. */
.classes {
  display: flex;
  gap: 14px;
  margin: 0 0 clamp(18px, 3vw, 32px);
  padding: 8px clamp(14px, 4vw, 40px) 18px;
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  overscroll-behavior-x: contain;
}

.classes::-webkit-scrollbar {
  display: none;
}

.slot {
  flex: 0 0 min(78vw, 400px);
  scroll-snap-align: center;
}

.card {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: 14px;
  background: #0d141c;
  overflow: hidden;
  color: #fff;
  box-shadow: 0 0 0 3px #0d141c, 0 0 0 6px #2c3b4c, 0 14px 22px rgba(0, 0, 0, 0.5);
  touch-action: manipulation;
  transition: transform 260ms var(--ease), box-shadow 260ms var(--ease), filter 260ms var(--ease);
  filter: saturate(0.55) brightness(0.78);
}

.card.on {
  transform: translateY(-8px);
  filter: none;
  box-shadow: 0 0 0 3px #0d141c, 0 0 0 6px #b98a3a, 0 0 0 9px #0d141c, 0 22px 30px rgba(0, 0, 0, 0.55);
}

.art {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 3 / 2;
  object-fit: cover;
}

.name {
  --h: 54px;
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  padding: 0 40px;
}

.name .k-title {
  font-size: clamp(1.3rem, 1.1rem + 1vw, 1.9rem);
  text-transform: uppercase;
}

.count {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 1.1rem;
}

@media (min-width: 900px) {
  .classes {
    justify-content: center;
    overflow: visible;
  }

  .slot {
    flex-basis: min(30vw, 400px);
  }
}

@media (hover: hover) {
  .card:hover:not(.on) {
    filter: saturate(0.85) brightness(0.92);
  }
}
</style>
